import util from '../util';
import Widget from './Widget';

export default class LatestEventWidget extends Widget {
	constructor(opts) {
		super(opts);
		this._eventFilter    = opts.eventFilter;
		this._isTopMode      = opts.isTopMode || false;
		this._topValue       = -Infinity;
		this._latestName     = null;
		this._latestAmount   = null;
		this._latestCurrency = null;
		this._latestAvatar   = null;
		this._latestService  = null;
	}

	applySettings(settings) {
		super.applySettings(settings);
		if(this.userData?.currency_code)
			this.settings.currency_code = this.userData.currency_code;
		this._render();
	}

	applyUserData(data) {
		super.applyUserData(data);
		if(this.settings && data?.currency_code) {
			this.settings.currency_code = data.currency_code;
			this._render();
		}
	}

	_build() {
		this.container.innerHTML = `
			<div class="le-widget">
				<div class="le-avatar-wrap">
					<img class="le-avatar" alt="" />
					<img class="le-service-icon" alt="" />
				</div>
				<div class="le-text">
					<div class="le-label"></div>
					<div class="le-name"></div>
					<div class="le-amount"></div>
				</div>
			</div>
		`;
	}

	_generateCSS() {
		const s = this.settings;
		const valign  = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }[s.verticalAlign] || 'center';
		const halign  = { left: 'flex-start', center: 'center', right: 'flex-end' }[s.textAlign] || 'flex-start';
		const shadow = (s.blurRadius > 0 || s.spreadRadius > 0)
			? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};`
			: '';
		const css = `
			#${this.container.id} {
				height: 100%;
				display: flex;
				flex-direction: column;
				justify-content: ${valign};
				${s.fontFamily ? `font-family: '${s.fontFamily}';` : 'font-family: Arial, sans-serif;'}
				font-size: ${s.fontSize || 40}px;
				color: ${s.textColor || '#ffffff'};
				${s.textAlign ? `text-align: ${s.textAlign};` : ''}
				${s.letterSpacing > 0 ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${s.boldText ? 'font-weight: bold;' : ''}
				${s.italicText ? 'font-style: italic;' : ''}
				${(s.underlineText || s.strikethroughText) ? `text-decoration: ${s.underlineText ? 'underline' : ''} ${s.strikethroughText ? 'line-through' : ''};` : ''}
				${s.uppercaseText ? 'text-transform: uppercase;' : ''}
			}
			#${this.container.id} .le-widget {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: ${halign};
				gap: .5em;
			}
			#${this.container.id} .le-avatar-wrap {
				position: relative;
				width: 2.5em;
				height: 2.5em;
				flex-shrink: 0;
			}
			#${this.container.id} .le-avatar {
				width: 100%;
				height: 100%;
				border-radius: 50%;
				object-fit: cover;
				display: block;
			}
			#${this.container.id} .le-service-icon {
				position: absolute;
				bottom: 0;
				right: 0;
				width: 38%;
				height: 38%;
				object-fit: contain;
			}
			#${this.container.id} .le-avatar-wrap--icon-only .le-service-icon {
				width: 100%;
				height: 100%;
			}
			#${this.container.id} .le-text { min-width: 0; }
			#${this.container.id} .le-label  { ${shadow} }
			#${this.container.id} .le-name   { ${shadow} }
			#${this.container.id} .le-amount  { ${shadow} }
			${s.extraCSS || ''}
		`;
		this._setCSS('latestEventWidgetStyle', css);
		this._updateFontLink();
	}

	onAlert(data) {
		if(!data?.message) return;
		const msg = data.message;
		if(!this._eventFilter(msg)) return;

		const amount   = msg.convertedValue || 0;
		const currency = msg.convertedCurrency || this.settings?.currency_code || 'USD';

		if(this._isTopMode && amount <= this._topValue) return;
		if(this._isTopMode) this._topValue = amount;

		this._latestName     = msg.sender?.displayName || msg.sender?.username || msg.sender?.token || '';
		this._latestAmount   = amount > 0 ? amount   : null;
		this._latestCurrency = amount > 0 ? currency : null;
		this._latestAvatar   = msg.sender?.avatar || null;
		this._latestService  = this.serviceIcons[msg.serviceId] || msg.serviceIconUrl || null;
		this._render();
	}

	_render() {
		if(!this.settings) return;
		const avatarWrapEl  = this.container.querySelector('.le-avatar-wrap');
		const avatarEl      = this.container.querySelector('.le-avatar');
		const serviceIconEl = this.container.querySelector('.le-service-icon');
		const labelEl       = this.container.querySelector('.le-label');
		const nameEl        = this.container.querySelector('.le-name');
		const amountEl      = this.container.querySelector('.le-amount');
		if(!labelEl || !nameEl || !amountEl) return;

		const hasAvatar  = !this.settings.hideAvatar && !!this._latestAvatar;
		const hasService = !this.settings.hideAvatar && !!this._latestService;

		if(avatarWrapEl) {
			avatarWrapEl.hidden = !(hasAvatar || hasService);
			avatarWrapEl.classList.toggle('le-avatar-wrap--icon-only', !hasAvatar && hasService);
		}
		if(avatarEl) {
			avatarEl.style.display = hasAvatar ? '' : 'none';
			avatarEl.src           = hasAvatar ? this._latestAvatar : '';
		}
		if(serviceIconEl) {
			serviceIconEl.hidden = !hasService;
			serviceIconEl.src    = hasService ? this._latestService : '';
		}

		labelEl.hidden      = !this.settings.showLabel;
		labelEl.textContent = this.settings.label || '';
		nameEl.textContent  = this._latestName || '';

		if(this._latestAmount != null) {
			amountEl.hidden      = false;
			amountEl.textContent = this.settings.hideCurrency
				? String(this._latestAmount)
				: util.generateValueString(this._latestAmount, this._latestCurrency || 'USD');
		} else {
			amountEl.hidden = true;
		}
	}
}
